import type { ModuleEntry, ModuleGroup } from './modules.tsx'
import type { WorkspaceModule } from './workspace.ts'

/**
 * Como o menu lateral se organiza — F132.
 *
 * Três listas a partir das telas que a pessoa pode ver:
 *
 * - **acesso rápido** — as telas do balcão (`COUNTER_MODULES`), na ordem delas;
 * - **grupos** — o índice do resto, por seção;
 * - **filhos** — as telas que moram sob outra (Preços e Estoque sob Produtos).
 *
 * Duas regras que o menu antigo quebrava:
 *
 * 1. **O que está no acesso rápido não se repete no grupo.** PDV, Orçamentos,
 *    Clientes e Produtos apareciam duas vezes — no recolhido, dois ícones
 *    iguais seguidos no trilho.
 * 2. **Filho sem pai visível sobe para o grupo.** Importar clientes mora sob
 *    Importar, que pede `catalog.write`; quem só tem `party.write` não vê o pai,
 *    e sem esta regra a tela sumiria do menu inteiro sem ninguém tirar.
 *
 * Um filho continua aparecendo sob o pai mesmo quando o pai está no acesso
 * rápido — é o caso de Produtos: Preços e Estoque ficam sob ele ali.
 */

export type SidebarSections = {
  readonly counter: readonly ModuleEntry[]
  readonly grouped: ReadonlyMap<ModuleGroup, readonly ModuleEntry[]>
  readonly nested: ReadonlyMap<WorkspaceModule, readonly ModuleEntry[]>
}

export const sidebarSections = (
  visible: readonly ModuleEntry[],
  counterModules: readonly WorkspaceModule[]
): SidebarSections => {
  const shown = new Set(visible.map((item) => item.module))

  const counter = counterModules
    .map((module) => visible.find((item) => item.module === module))
    .filter((item): item is ModuleEntry => item !== undefined)
  const inCounter = new Set(counter.map((item) => item.module))

  const nested = new Map<WorkspaceModule, ModuleEntry[]>()
  const grouped = new Map<ModuleGroup, ModuleEntry[]>()

  for (const item of visible) {
    // Início mora no topo — no logotipo e no acesso rápido (F215) —, e não
    // no índice.
    if (item.module === 'inicio') continue

    const parent = item.parent
    if (parent !== undefined && shown.has(parent)) {
      const list = nested.get(parent)
      if (list === undefined) nested.set(parent, [item])
      else list.push(item)
      continue
    }

    if (inCounter.has(item.module)) continue

    const list = grouped.get(item.group)
    if (list === undefined) grouped.set(item.group, [item])
    else list.push(item)
  }

  return { counter, grouped, nested }
}
